import { useEffect } from "react";
import { useRouter } from "next/router";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { IExtendedSearchData } from "src/api/server/extended-search";
import { ISelectedRanking } from "src/components/pages/search/components/types";
import { ISearch } from "src/components/pages/search/search.types";
import { handleSearch, setNewSearchStoreValues, setDefaultFilters } from "src/components/pages/search/store/actions";
import { fixSearchUrlQuery } from "src/components/pages/search/utils/fix-search-url-query";
import { mapSearchParamsToState } from "src/components/pages/search/utils/map-query-string-to-state";
import { mapSearchTermToQuery } from "src/components/pages/search/utils/map-search-term";
import { mapSelectedRankingToQuery } from "src/components/pages/search/utils/map-selected-ranking";
import { mapSortDirectionToQuery } from "src/components/pages/search/utils/map-sort-direction";
import { urlSearchParamsToQueryString, getNormalizedRouterQuery } from "src/components/pages/search/utils/url-params-helpers";
import { COUNTRY_PARAMETER } from "src/consts/locale";
import { EnumCountryKey } from "src/types/common-enum";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { sanitizeSearchTerm } from "src/utils/sanitize-search-term";
import { mapIsAtInvestableToQuery, mapIsChInvestableToQuery, mapIsInvestableToQuery } from "../utils/map-is-investable";

interface IUseFetchResultsOnPageLoad extends ISearch {
    ranking: ISelectedRanking;
    extendedSearchData: IExtendedSearchData;
}

export const useFetchResultsOnPageLoad = ({ ranking, extendedSearchData, onSearch }: IUseFetchResultsOnPageLoad) => {
    const router = useRouter();
    const { query } = router;
    const { country } = useRouterLocale();
    const { searchTerm } = getNormalizedRouterQuery(query);
    const genericActivity = useGenericActivityFromElysiumMutation();

    const atInvestableQuery = country === EnumCountryKey.AT ? `${mapIsAtInvestableToQuery(true, country)}` : "";
    const chInvestableQuery = country === EnumCountryKey.CH ? `${mapIsChInvestableToQuery(true, country)}` : "";
    const investableQuery = mapIsInvestableToQuery(country !== EnumCountryKey.INT);

    const getQueryParams = () => {
        const searchTermParam = searchTerm ? `${mapSearchTermToQuery(sanitizeSearchTerm(searchTerm))}` : "";
        const rankingSorted = searchTerm
            ? ""
            : `${mapSortDirectionToQuery(ranking.sortDirection)}&${mapSelectedRankingToQuery(ranking, extendedSearchData.rankingsData)}`;

        return [searchTermParam, investableQuery, atInvestableQuery, chInvestableQuery, rankingSorted].filter(Boolean).join("&");
    };

    const queryParams = getQueryParams();

    const getOriginPathName = () => {
        const { origin, pathname } = window.location;
        return `${origin}${pathname}?`;
    };

    const trackGenericActivity = (queryParams: string) =>
        genericActivity({
            activityTypeId: EnumActivityType.VisitWikifolioSearchPage,
            parameter: queryParams,
            referrer: `${getOriginPathName()}${queryParams}`,
        });

    const updateUrl = () => router.push(`${getOriginPathName()}${queryParams}`);

    const handleOnNoneParameters = () => {
        setDefaultFilters(ranking, country);
        updateUrl();
        trackGenericActivity(queryParams);
    };

    const handleOnExistingParameters = () => {
        const { pathname, href } = window.location;
        const fixedUrlSearchParams = fixSearchUrlQuery(query, country, extendedSearchData);
        const fixedQueryParams = urlSearchParamsToQueryString(fixedUrlSearchParams);
        router.replace({ pathname, query: fixedQueryParams, href }, undefined, { shallow: true });

        const queryParamKeys = Object.keys(query).filter(key => key !== COUNTRY_PARAMETER);
        const isOnlySearchTerm = queryParamKeys.length === 1 && queryParamKeys.indexOf("searchTerm") === 0;

        const newState = mapSearchParamsToState(fixedUrlSearchParams, extendedSearchData);

        if (isOnlySearchTerm) {
            updateUrl();
            setNewSearchStoreValues({
                ...newState,
                isInvestable: Boolean(investableQuery),
                isAtInvestable: Boolean(atInvestableQuery),
                isChInvestable: Boolean(chInvestableQuery),
                query: sanitizeSearchTerm(searchTerm),
                sortDirection: null,
                selectedRanking: { id: null, sortDirection: null },
            });

            trackGenericActivity(queryParams);
        } else {
            setNewSearchStoreValues(newState);
            trackGenericActivity(fixedQueryParams);
        }
    };

    useEffect(() => {
        const areQueryParamsPresent = Object.keys(router.query).some(key => key !== COUNTRY_PARAMETER);
        if (areQueryParamsPresent) {
            handleOnExistingParameters();
        } else {
            handleOnNoneParameters();
        }

        handleSearch({ onSearch });
        // We disable the rule because the hook HAS to be run only once - on page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
